const en = {
  common: {
    loading: "Loading",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    back: "Back",
    download: "Download",
    upload: "Upload",
    search: "Search",
    add: "Add",
    remove: "Remove",
    yes: "Yes",
    no: "No",
    optional: "Optional",
    confirm: "Confirm",
    error: "Unexpected error. Please try again.",
    no_results: "No results.",
    actions: "Actions",
    premium_only: "Available on the Premium plan only",
    upgrade_to_unlock: "Upgrade to Premium to unlock",
  },
  nav: {
    dashboard: "Dashboard",
    events: "Events",
    clients: "Clients",
    calendar: "Calendar",
    suppliers: "Address Book",
    guide: "Guide",
    settings: "Settings",
    logout: "Sign out",
    dark: "Dark",
    light: "Light",
  },
  auth: {
    forgot_password: "Forgot your password?",
    forgot_title: "Recover access",
    forgot_subtitle: "Enter your account email: we will send you a link to choose a new password.",
    forgot_submit: "Send me the link",
    forgot_sending: "Sending…",
    forgot_sent_title: "Check your inbox",
    forgot_sent_body: "If the address matches an account, you will receive an email with the link shortly. It lasts one hour and works once.",
    forgot_back: "Back to sign in",
    reset_title: "Choose a new password",
    reset_subtitle: "At least 8 characters.",
    reset_password: "New password",
    reset_confirm: "Repeat password",
    reset_mismatch: "The two passwords do not match.",
    reset_too_short: "Password must be at least 8 characters.",
    reset_submit: "Save password",
    reset_saving: "Saving…",
    reset_done: "Password updated. You can sign in now.",
    reset_invalid_title: "Link no longer valid",
    reset_invalid_body: "This link has expired or has already been used. Request a new one.",
    reset_request_new: "Request a new link",
    reset_checking: "Checking the link…",
    welcome_back: "Welcome back",
    fill_credentials: "Please enter email and password.",
    invalid_email: "Invalid email address.",
    login_title: "Sign in to your studio",
    login_subtitle: "The all-in-one CRM crafted for professional wedding planners.",
    email: "Email",
    password: "Password",
    login: "Sign in",
    logging_in: "Signing in…",
    no_account: "Don't have an account?",
    create_account: "Create your studio",
    have_account: "Already have an account?",
    register_title: "Create your studio",
    name: "Name",
    creating: "Creating…",
    create_button: "Create account",
    welcome_demo: "Demo",
  },
  dashboard: {
    title: "Good morning",
    studio: "Studio",
    all_events: "All events",
    active_events: "Active events",
    budget_engaged: "Budget engaged",
    paid_suppliers: "Paid to vendors",
    contracts_signed: "Confirmed quotes",
    tasks_due: "Tasks due",
    of_total: "of {{total}} total",
    of_budget: "{{pct}}% of budget",
    cumulated: "cumulative",
    signed_ratio: "signed ratio",
    next_7_days: "next 7 days",
    next_payment: "Next payment due",
    deadline: "Deadline",
    upcoming_events: "Upcoming events",
    all: "All",
    recent_payments: "Recent payments",
    no_events: "No scheduled events.",
    no_payments: "No payments yet.",
    categories_chart: "Quotes by category",
    no_quotes: "No quotes yet.",
  },
  events: {
    title: "Events & Weddings",
    count_one: "{{n}} event",
    count_other: "{{n}} events",
    concluded: "Completed",
    upcoming_section: "Upcoming",
    concluded_section: "Archived",
    new_event: "New Event",
    new_event_title: "New event",
    invalid_budget: "Budget cannot be negative. Please enter a value ≥ 0.",
    budget_required: "Please enter the event budget.",
    invalid_name: "Please enter the event name.",
    invalid_date: "Event date is required.",
    past_date_warning: "The event date is in the past.",
    link_client_label: "Linked client (optional)",
    link_client_placeholder: "Pick a CRM client…",
    link_client_none: "No linked client",
    link_client_all_taken: "All CRM clients already have a linked event.",
    event_name: "Event name",
    date: "Date",
    location: "Location",
    budget_total: "Total budget (€)",
    notes: "Notes",
    no_events: "No events",
    no_events_subtitle: "Start by creating your first wedding.",
    delete_event: "Delete event",
    delete_confirm: "This will permanently delete the event and all linked data: suppliers, payments, tasks and guests. This action cannot be undone.",
    deleted: "Event deleted",
    location_placeholder: "Location TBD",
    invitees: "Groups confirmed",
    tasks: "Tasks",
    suppliers: "Vendors",
    budget: "Budget",
    residual: "Remaining",
    paid_short: "Paid",
    tab: {
      panoramica: "Overview",
      budget: "Budget",
      fornitori: "Vendors",
      pagamenti: "Payments",
      invitati: "Guests",
      tavoli: "Seating",
      attivita: "Tasks",
      timeline: "Timeline",
      file: "Files",
      contratti: "Contracts",
      moodboard: "Moodboard",
      portale: "Portal",
    },
    overview: {
      budget: "Budget",
      total: "Total",
      quoted: "Quoted",
      committed: "Committed",
      paid: "Paid",
      remaining: "Remaining",
      over_budget: "Over budget",
      invitees: "Guests",
      confirmed: "Confirmed (groups)",
      pending: "Pending (groups)",
      refused: "Declined (groups)",
      total_people: "Total people",
      summary: "Summary",
      tasks_done: "Tasks done",
      next_deadlines: "Next deadlines",
      no_deadlines: "No upcoming deadlines.",
      no_quotes: "No quotes.",
      quotes_by_cat: "Quotes by category",
    },
    budget_tab: {
      total: "Total budget",
      saved: "Budget updated",
      invalid_negative: "Budget cannot be negative. Please enter a value ≥ 0.",
      paid_total: "Paid / Total",
      export_csv: "Export CSV",
      export_pdf: "Export PDF",
      remaining_budget: "Available budget",
      remaining_budget_hint: "Budget you can still assign to suppliers (Total − Quoted).",
      remaining_budget_over: "Over budget: quotes exceed the total budget.",
      remaining_pay: "Outstanding balance",
      remaining_pay_hint: "Amount still owed on signed contracts (Committed − Paid).",
      remaining_pay_overpaid: "Paid exceeds committed",
      committed_legend: "Committed = sum of quotes whose contract is SIGNED. Change the status to 'Firmato' in the Suppliers tab to auto-populate this value.",
    },
    suppliers_tab: {
      linked: "Linked vendors ({{n}})",
      link_supplier: "Link Vendor",
      link_title: "Link vendor",
      from_address_book: "Vendor (from Address Book)",
      select: "Select…",
      all_linked: "All vendors are already linked",
      all_linked_hint: "No additional vendor available in your Address Book.",
      quote: "Quote (€)",
      contract: "Contract",
      no_suppliers: "No vendors linked. Add one from the address book.",
      select_supplier_first: "Select a vendor",
      linked_ok: "Vendor linked",
      unlinked_ok: "Vendor unlinked from the event",
      delete_link_title: "Unlink vendor?",
      delete_link_confirm: "Unlink this vendor from the event? Related installments and contracts will not be deleted.",
      link_button: "Link",
      preview_label: "Quote",
    },
    payments_tab: {
      quoted: "Quoted",
      committed: "Committed",
      paid: "Paid",
      residual: "Remaining",
      invalid_supplier: "Please enter the vendor name.",
      invalid_amount: "Amount must be greater than 0.",
      add_installment: "Add installment",
      add_title: "New installment",
      supplier_name: "Vendor name",
      type: "Type",
      amount: "Amount €",
      due_date: "Due date",
      status: "Status",
      iban: "IBAN (opt.)",
      all_statuses: "All statuses",
      no_installments: "No installments.",
      installment_added: "Installment added",
      export_csv: "Export CSV",
      export_pdf: "Export PDF",
      amount_label: "Amount",
    },
    guests_tab: {
      people: "People",
      confirmed: "Confirmed (people)",
      groups: "groups",
      pending: "Pending (groups)",
      refused: "Declined (groups)",
      all_sources: "All sources",
      manual: "Manual",
      website: "Website",
      csv: "CSV",
      new_guest: "New guest",
      add_title: "Add guest",
      name: "Name",
      email: "Email",
      pax: "Pax",
      no_guests: "No guests yet.",
      guest_added: "Guest added",
      guest_updated: "Guest updated",
      email_collision_title: "Email already used by another guest",
      email_collision_desc: "A guest is already linked to this email on this event. What would you like to do?",
      email_collision_merge: "Update existing",
      email_collision_create: "Add anyway",
      import_csv: "Import CSV",
      import_csv_title: "Import guests from CSV",
      import_csv_help: "Upload a CSV with columns: name, email, pax, rsvp_status, diet_tags, notes.",
      import_csv_done: "Imported {{ok}} guests ({{err}} errors)",
      public_link: "Public RSVP link",
      copy_link: "Copy link",
      link_copied: "Link copied to clipboard",
    },
    tables_tab: {
      title: "Seating Chart",
      add_table: "New table",
      edit_table: "Edit table",
      table_name: "Table name",
      capacity: "Capacity",
      shape: "Shape",
      round: "Round",
      rectangular: "Rectangular",
      rotation: "Rotation",
      shape_round: "Round",
      shape_rectangular: "Rectangular / Imperial",
      shape_square: "Square",
      shape_oval: "Oval",
      shape_crescent: "Crescent",
      shape_horseshoe: "Horseshoe",
      shape_custom: "Custom shape",
      zoom_in: "Zoom in",
      zoom_out: "Zoom out",
      zoom_fit: "Fit to page",
      guests_at_table: "Guests at table",
      toggle_background: "Show/hide floor plan background",
      no_tables: "No tables yet. Create the first one to start seating guests.",
      no_floorplan_hint: "Upload a venue floor plan or add tables manually",
      move_hint: "Drag to move the table",
      seated: "Seated",
      drag_help: "Drag guests onto tables. Use the handle to move tables on the floor plan.",
      unassigned: "Unassigned",
      export_pdf: "Export PDF chart",
      saved: "Saved",
    },
    floorplan: {
      upload: "Upload floor plan",
      replace: "Replace floor plan",
      remove: "Remove floor plan",
      uploading: "Uploading…",
      uploaded: "Floor plan uploaded",
      delete_confirm: "Remove the venue floor plan?",
      generate: "Generate tables with AI",
      analyzing: "Reading floor plan…",
      no_tables_found: "No tables recognized in the plan. Add them manually.",
      draft_title: "Recognized tables",
      draft_help: "Deselect the ones you don't want. You can move and fix them afterwards.",
      tables_added: "{n} tables added",
      confirm: "Confirm {n} tables",
      convert_error: "Could not read the file. Use a valid PDF or image.",
      choose_page: "Which page contains the floor plan?",
      use_this_page: "Use this page",
      include_elements: "Also import room and fixed elements ({n}: dance floor, stage, buffet…)",
      from_cache: "Plan already analyzed: cached result, no AI cost",
      reanalyze: "Re-analyze",
      notes_placeholder: "Hints for the AI (optional) — e.g.: tables are the white shapes: one crescent at the top and two serpentines on the sides; green circles are trees, not tables.",
    },
    tasks_tab: {
      add: "New task",
      add_title: "New task",
      title_label: "Title",
      due_date: "Due",
      todo: "To Do",
      doing: "In Progress",
      done: "Done",
      none: "None",
      created: "Task created",
    },
    timeline_tab: {
      add: "New entry",
      add_title: "New timeline entry",
      time: "Time (HH:MM)",
      title_label: "Title",
      category_label: "Moment",
      category_placeholder: "Select a moment…",
      custom_title_label: "Custom title",
      location: "Location",
      description: "Description",
      no_items: "No entries yet. Add the first one to build the schedule.",
      saved: "Entry saved",
      edit: "Edit timeline entry",
      edit_title: "Edit timeline entry",
      updated: "Entry updated",
    },
    timeline_categories: {
      welcome: "Welcome / Guests arrival",
      cerimonia: "Ceremony",
      aperitivo: "Aperitif / Cocktail",
      foto_sposi: "Couple photos",
      pranzo: "Lunch",
      cena: "Dinner",
      taglio_torta: "Cake cutting",
      lancio_bouquet: "Bouquet toss",
      primo_ballo: "First dance",
      open_bar: "Open bar",
      animazione: "Entertainment / DJ set",
      confettata: "Sugared almonds bar",
      sweet_table: "Sweet table",
      lancio_riso: "Rice throwing",
      saluti: "Final farewells",
      tradizioni: "Traditions",
      custom: "Custom",
    },
    files_tab: {
      drag_drop: "Drag files here or click to select",
      no_files: "No files uploaded.",
      uploaded: "File uploaded",
      size: "Size",
      uploaded_at: "Uploaded on",
    },
    contracts_tab: {
      placeholder_title: "Contracts",
      placeholder_desc: "Upload PDFs and send for e-signature. Coming in phase 3.",
      title: "Vendor contracts",
      event_hint: "Upload contracts here, under each vendor linked to this event — they stay tied to this wedding. (The contract status — To Confirm/Confirmed/Paid — is set in the Vendors tab instead.)",
      empty_title: "No vendors linked",
      empty_subtitle_event: "First link vendors to this event from the Vendors tab: you'll then be able to upload their contracts here.",
      count: "{{n}} contract(s)",
      download: "Download",
      upload: "Upload contract",
      uploading: "Uploading…",
      remove: "Delete",
      delete_confirm: "Delete this contract?",
      format_hint: "PDF, DOCX or CSV.",
      no_contracts: "No contract uploaded for this vendor.",
    },
    moodboard_tab: {
      title: "Inspiration moodboard",
      subtitle: "Upload reference images (palette, venue, details) you can share with the couple.",
      add_image: "Add image",
      drag_drop: "Drop one or more images here or click this area to choose files",
      uploading: "Uploading…",
      uploaded: "Image uploaded",
      uploaded_many: "{{n}} images uploaded",
      rejected_files: "{{n}} files skipped: use images up to 50 MB",
      delete_confirm: "Delete this image from the moodboard?",
      delete_gallery: "Delete gallery",
      delete_gallery_confirm: "Delete all gallery images? This action cannot be undone.",
      gallery_deleted: "Gallery deleted",
      gallery_count: "{{n}} images",
      invalid_format: "Invalid format — please use PNG, JPG or WEBP.",
      empty_title: "Empty moodboard",
      empty_subtitle: "Add the first image to start building the event's vision.",
      edit_title: "Edit image",
      image_title: "Title",
      category: "Category",
      caption: "Note",
      updated: "Image updated",
      share: "Share",
      canva_title: "Canva project",
      canva_subtitle: "Save the Canva project link used for this event. The link stays private to your workspace.",
      open_canva: "Open Canva",
      canva_saved: "Canva link saved",
      delete_canva: "Remove link",
      delete_canva_confirm: "Remove the Canva link from this event?",
      canva_deleted: "Canva link removed",
      preview_unavailable_title: "Canva preview unavailable",
      preview_unavailable: "The project is hosted by Canva and this link doesn't provide an embeddable preview. Aurea Pro is working correctly: open the project directly in Canva or create a new public link.",
      saving: "Saving…",
      previous: "Previous image",
      next: "Next image",
    },
  },
};

export default en;
